import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Alert, AlertType, ButtonLink } from '@toggle/design-system';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { ToastPosition, useToasts } from '~/stores/use-toasts/useToasts';
import {
  getCustomSources,
  resetCustomSources,
  updateCustomSources,
} from '~/views/alfred/research/services/conversations-service';

import { formatWebsiteInfo } from '../utils/formatWebsiteInfo';
import {
  filterBlacklistedSources,
  isUrlBlacklisted,
  WebsiteInfo,
} from './blacklistedUrlsUtils';
import { InlineAlert } from './InlineAlert';
import { LinkInput } from './LinkInput';
import { DroppableArea, MAX_CUSTOM_SOURCES } from './SourcesDraggableArea';
import { SourcesUpdateAlert } from './SourcesUpdateAlert';
import * as S from './TrustedSourcesModal.styles';

interface TrustedSourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TrustedSourcesModal = ({
  isOpen,
  onClose,
}: TrustedSourcesModalProps) => {
  const { t } = useTranslation(['chat', 'common']);
  const { trackAlfredTrustedSourceAdded, trackAlfredTrustedSourceRemoved } =
    usePosthogEvents();

  const [customSources, setCustomSources] = useState<WebsiteInfo[]>([]);
  const [resetting, setResetting] = useState(false);
  const [isOnlySource, setIsOnlySource] = useState(false);
  const sourcesContainerRef = useRef<HTMLDivElement>(null);

  const showToast = useToasts(state => state.showToast);

  const handleAddWebsites = (websites: WebsiteInfo[]) => {
    const existedSources = customSources.map(source => source.url);
    const uniqueWebsites = websites.filter(
      website => !existedSources.includes(website.url)
    );
    const newSources = [...customSources, ...uniqueWebsites];
    setCustomSources(newSources);

    if (uniqueWebsites.length > 0) {
      trackAlfredTrustedSourceAdded(POSTHOG_TRACKING_SOURCES.ALFRED);
    }

    if (newSources.length > 1) {
      setIsOnlySource(false);
    }
  };

  const handleResetSources = async () => {
    const response = await resetCustomSources();

    if (response) {
      setResetting(false);
      onClose();
    }

    if (response.data) {
      showToast({
        id: 'reset-sources-success',
        position: ToastPosition.TopRight,
        content: <SourcesUpdateAlert updateType="reset" />,
      });
    }
  };

  const handleUpdateCustomSources = async () => {
    const blacklistedSource = filterBlacklistedSources(customSources);

    if (blacklistedSource.length > 0) {
      showToast({
        id: 'update-sources-error',
        position: ToastPosition.TopRight,
        content: (
          <Alert
            type={AlertType.Error}
            title={t('chat:trustedSources.blacklisted', {
              domain: blacklistedSource.map(source => source.url).join(', '),
              count: blacklistedSource.length,
            })}
          />
        ),
      });
    }

    const response = await updateCustomSources({
      sources: customSources
        .filter(source => !isUrlBlacklisted(source.url))
        .map(source => source.url),
    });

    if (response) {
      onClose();
    }

    if (response.data) {
      showToast({
        id: 'update-sources-success',
        position: ToastPosition.TopRight,
        content: <SourcesUpdateAlert updateType="update" />,
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over) {
      setCustomSources(currentSources => {
        const activeIndex = currentSources.findIndex(
          source => source.id === active.id
        );
        const overIndex = currentSources.findIndex(
          source => source.id === over.id
        );

        if (activeIndex === -1 || overIndex === -1) {
          return currentSources;
        }

        return arrayMove(currentSources, activeIndex, overIndex);
      });
    }
  };

  const handleRemoveSource = (id: string) => {
    if (customSources.length === 1) {
      setIsOnlySource(true);
    } else {
      trackAlfredTrustedSourceRemoved(POSTHOG_TRACKING_SOURCES.ALFRED);
      setCustomSources(customSources.filter(source => source.id !== id));
    }
  };

  useEffect(() => {
    const fetchCustomSources = async () => {
      const result = await getCustomSources();

      if (result.data?.sources) {
        const existedSources = customSources.map(source => source.url);
        const uniqueSources = result.data.sources.filter(
          source => !existedSources.includes(source)
        );
        const uniqueWebsiteInfos = formatWebsiteInfo(uniqueSources);
        setCustomSources(uniqueWebsiteInfos);
      }
    };

    fetchCustomSources();
  }, []);

  const getModalProps = () => {
    if (resetting) {
      return {
        title: t('chat:trustedSources.modal.resetTitle'),
        primaryBtnProps: {
          label: t('chat:trustedSources.modal.saveTrustedSources'),
          onClick: handleResetSources,
        },
        cancelBtnProps: {
          label: t('common:cancel'),
          onClick: () => setResetting(false),
        },
      };
    }

    return {
      title: t('chat:trustedSources.modal.title'),
      description: (
        <p>
          {t('chat:trustedSources.modal.description_1')}
          <br />
          <br />
          {t('chat:trustedSources.modal.description_2')}
        </p>
      ),
      primaryBtnProps: {
        label: t('common:update'),
        onClick: handleUpdateCustomSources,
      },
      cancelBtnProps: {
        label: t('common:cancel'),
        onClick: onClose,
      },
    };
  };

  return (
    <S.StyledModal
      isOpen={isOpen}
      onClose={() => {
        setCustomSources([]);
        onClose();
      }}
      {...getModalProps()}
      disableEnterClose
      outsideClickClose={false}
      data-testid="trusted-sources-modal"
      footerLeftAction={
        resetting ? null : (
          <ButtonLink
            label={t('chat:trustedSources.modal.reset')}
            onClick={() => setResetting(true)}
          />
        )
      }
    >
      <S.Content>
        {resetting ? (
          <S.ResetDescription>
            {t('chat:trustedSources.modal.resetDescription')}
          </S.ResetDescription>
        ) : (
          <>
            <S.Sources ref={sourcesContainerRef}>
              <DndContext onDragEnd={handleDragEnd}>
                <SortableContext
                  items={customSources.map(source => source.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableArea
                    items={customSources}
                    handleRemove={handleRemoveSource}
                  />
                </SortableContext>
              </DndContext>
              {isOnlySource && <InlineAlert alertType="onlyOneSource" />}
            </S.Sources>
            {customSources.length >= MAX_CUSTOM_SOURCES ? (
              <InlineAlert alertType="exceededLimit" />
            ) : (
              <LinkInput
                sourceCount={customSources.length}
                handleAddWebsites={handleAddWebsites}
              />
            )}
          </>
        )}
      </S.Content>
    </S.StyledModal>
  );
};
